/**
 * Firestore path helpers and collection references
 *
 * This module provides type-safe functions for constructing
 * Firestore paths and managing collections.
 */

/**
 * Get path to a user document
 *
 * @example
 * userPath("uid123") // "/users/uid123"
 */
export function userPath(uid: string): string {
  return `/users/${uid}`;
}

/**
 * Get path to a project document
 *
 * @example
 * projectPath("uid123", "proj456") // "/users/uid123/projects/proj456"
 */
export function projectPath(userId: string, projectId: string): string {
  return `${userPath(userId)}/projects/${projectId}`;
}

/**
 * Get path to project's version subcollection
 *
 * @example
 * projectVersionsPath("uid123", "proj456") // "/users/uid123/projects/proj456/versions"
 */
export function projectVersionsPath(userId: string, projectId: string): string {
  return `${projectPath(userId, projectId)}/versions`;
}

/**
 * Get path to a specific version
 *
 * @example
 * versionPath("uid123", "proj456", "v1") // "/users/uid123/projects/proj456/versions/v1"
 */
export function versionPath(userId: string, projectId: string, versionId: string): string {
  return `${projectVersionsPath(userId, projectId)}/${versionId}`;
}

/**
 * Get path to user's projects subcollection
 *
 * @example
 * userProjectsPath("uid123") // "/users/uid123/projects"
 */
export function userProjectsPath(userId: string): string {
  return `${userPath(userId)}/projects`;
}

/**
 * Get path to user's PDF documents subcollection
 *
 * @example
 * userPdfDocumentsPath("uid123") // "/users/uid123/pdfDocuments"
 */
export function userPdfDocumentsPath(userId: string): string {
  return `${userPath(userId)}/pdfDocuments`;
}

/**
 * Get path to a PDF document
 *
 * @example
 * pdfDocumentPath("uid123", "doc456") // "/users/uid123/pdfDocuments/doc456"
 */
export function pdfDocumentPath(userId: string, docId: string): string {
  return `${userPdfDocumentsPath(userId)}/${docId}`;
}

/**
 * Get path to PDF page annotations subcollection
 *
 * @example
 * pdfDocumentPagesPath("uid123", "doc456") // "/users/uid123/pdfDocuments/doc456/pages"
 */
export function pdfDocumentPagesPath(userId: string, docId: string): string {
  return `${pdfDocumentPath(userId, docId)}/pages`;
}

/**
 * Get path to one PDF page's annotations
 *
 * @example
 * pdfDocumentPagePath("uid123", "doc456", 0) // "/users/uid123/pdfDocuments/doc456/pages/0"
 */
export function pdfDocumentPagePath(
  userId: string,
  docId: string,
  pageIndex: number
): string {
  return `${pdfDocumentPagesPath(userId, docId)}/${pageIndex}`;
}

/**
 * Get path to user's PDF Studio signatures subcollection
 *
 * @example
 * userSignaturesPath("uid123") // "/users/uid123/signatures"
 */
export function userSignaturesPath(userId: string): string {
  return `${userPath(userId)}/signatures`;
}

/**
 * Get path to one saved signature
 *
 * @example
 * signaturePath("uid123", "sig456") // "/users/uid123/signatures/sig456"
 */
export function signaturePath(userId: string, signatureId: string): string {
  return `${userSignaturesPath(userId)}/${signatureId}`;
}

/**
 * Get path to a template document
 *
 * @example
 * templatePath("tpl789") // "/templates/tpl789"
 */
export function templatePath(templateId: string): string {
  return `/templates/${templateId}`;
}

/**
 * Get path to templates collection
 *
 * @example
 * templatesPath() // "/templates"
 */
export function templatesPath(): string {
  return '/templates';
}

/**
 * Get path to user statistics document
 *
 * @example
 * userStatsPath("uid123") // "/users/uid123/stats"
 */
export function userStatsPath(userId: string): string {
  return `${userPath(userId)}/stats`;
}

/**
 * Get path to user activity log
 *
 * @example
 * userActivityPath("uid123") // "/users/uid123/activity"
 */
export function userActivityPath(userId: string): string {
  return `${userPath(userId)}/activity`;
}

/**
 * Get path to comments on a project
 *
 * @example
 * projectCommentsPath("uid123", "proj456") // "/users/uid123/projects/proj456/comments"
 */
export function projectCommentsPath(userId: string, projectId: string): string {
  return `${projectPath(userId, projectId)}/comments`;
}

/**
 * Get path to a specific comment
 *
 * @example
 * commentPath("uid123", "proj456", "cmt789") // "/users/uid123/projects/proj456/comments/cmt789"
 */
export function commentPath(userId: string, projectId: string, commentId: string): string {
  return `${projectCommentsPath(userId, projectId)}/${commentId}`;
}

/**
 * Get path to collaborators on a project
 *
 * @example
 * projectCollaboratorsPath("uid123", "proj456") // "/users/uid123/projects/proj456/collaborators"
 */
export function projectCollaboratorsPath(userId: string, projectId: string): string {
  return `${projectPath(userId, projectId)}/collaborators`;
}

/**
 * Get path to a specific collaborator
 *
 * @example
 * collaboratorPath("uid123", "proj456", "user789") // "/users/uid123/projects/proj456/collaborators/user789"
 */
export function collaboratorPath(
  userId: string,
  projectId: string,
  collaboratorId: string
): string {
  return `${projectCollaboratorsPath(userId, projectId)}/${collaboratorId}`;
}

/** Collection names for easy reference */
export const COLLECTIONS = {
  USERS: 'users',
  PROJECTS: 'projects',
  TEMPLATES: 'templates',
  VERSIONS: 'versions',
  COMMENTS: 'comments',
  COLLABORATORS: 'collaborators',
  ACTIVITY: 'activity',
  STATS: 'stats',
  PDF_DOCUMENTS: 'pdfDocuments',
  PDF_PAGES: 'pages',
  SIGNATURES: 'signatures',
} as const;

/** Type for valid collection names */
export type CollectionName = (typeof COLLECTIONS)[keyof typeof COLLECTIONS];

/**
 * Parse a Firestore path to extract IDs
 *
 * @example
 * parsePath("/users/uid123/projects/proj456")
 * // { userId: "uid123", projectId: "proj456" }
 */
export function parsePath(path: string): {
  userId?: string;
  projectId?: string;
  versionId?: string;
  templateId?: string;
  commentId?: string;
} {
  const parts = path.split('/').filter(Boolean);
  const result: Record<string, string> = {};

  for (let i = 0; i < parts.length; i += 2) {
    const collection = parts[i];
    const id = parts[i + 1];

    switch (collection) {
      case 'users':
        result.userId = id;
        break;
      case 'projects':
        result.projectId = id;
        break;
      case 'versions':
        result.versionId = id;
        break;
      case 'templates':
        result.templateId = id;
        break;
      case 'comments':
        result.commentId = id;
        break;
    }
  }

  return result as {
    userId?: string;
    projectId?: string;
    versionId?: string;
    templateId?: string;
    commentId?: string;
  };
}
